import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Archive } from "lucide-react";
import api from "../lib/api";
import { CATEGORY_LABELS } from "../lib/constants";

export default function ArchivePage() {
    const [items, setItems] = useState([]);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(false);
    const [loading, setLoading] = useState(true);

    const fetchPage = useCallback(async (p) => {
        const { data } = await api.get("/archive", { params: { page: p, page_size: 15 } });
        return data;
    }, []);

    useEffect(() => {
        setLoading(true);
        fetchPage(1).then((data) => {
            setItems(data.items);
            setHasMore(data.has_more);
        }).finally(() => setLoading(false));
    }, [fetchPage]);

    const loadMore = async () => {
        const next = page + 1;
        const data = await fetchPage(next);
        setItems((prev) => [...prev, ...data.items]);
        setPage(next);
        setHasMore(data.has_more);
    };

    return (
        <div className="max-w-[1100px] mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="archive-page">
            <div className="panel p-8 sm:p-10 mb-10">
                <span className="grid place-items-center w-11 h-11 rounded-xl bg-accent-soft text-accent">
                    <Archive size={20} />
                </span>
                <span className="chip mt-5">The Archive</span>
                <h1 className="font-display text-3xl sm:text-5xl font-bold tracking-[-0.035em] mt-2.5">Past Editions</h1>
                <p className="mt-4 text-sm text-dim max-w-xl leading-relaxed">
                    Stories older than seven days are moved here. Archived pieces are retained for 30 days before removal.
                </p>
            </div>

            {loading ? (
                <p className="flex items-center justify-center gap-2 text-sm font-medium text-dim py-20">
                    <span className="w-1.5 h-1.5 rounded-full bg-accent animate-pulse-dot" />
                    Loading archive…
                </p>
            ) : items.length === 0 ? (
                <div className="panel p-14 text-center" data-testid="archive-empty">
                    <p className="text-sm text-dim">The archive is currently empty.</p>
                </div>
            ) : (
                <>
                    <ul className="divide-y divide-line border-y border-line">
                        {items.map((a) => (
                            <li key={a.id}>
                                <Link
                                    to={`/article/${a.id}`}
                                    data-testid={`archive-item-${a.id}`}
                                    className="group flex items-center gap-4 py-4 px-3 -mx-3 rounded-lg hover:bg-surface-raised transition-colors duration-150"
                                >
                                    <span className="font-mono text-xs text-dim w-24 shrink-0 hidden sm:block tabular-nums">
                                        {a.published_at
                                            ? new Date(a.published_at).toLocaleDateString("en-US", { month: "short", day: "2-digit", year: "numeric" })
                                            : ""}
                                    </span>
                                    <span className="hidden md:inline-flex shrink-0 w-32">
                                        <span className="chip !text-[10px] max-w-full truncate">
                                            {CATEGORY_LABELS[a.category] || a.category}
                                        </span>
                                    </span>
                                    <span className="font-display text-[15px] sm:text-base font-semibold leading-snug tracking-[-0.01em] flex-1 text-foreground group-hover:text-accent transition-colors duration-150">
                                        {a.title}
                                    </span>
                                    <ArrowUpRight
                                        size={16}
                                        className="shrink-0 text-line-strong opacity-0 -translate-x-1 transition-[opacity,transform,color] duration-150 group-hover:opacity-100 group-hover:translate-x-0 group-hover:text-accent"
                                    />
                                </Link>
                            </li>
                        ))}
                    </ul>
                    {hasMore && (
                        <div className="mt-12 text-center">
                            <button
                                onClick={loadMore}
                                data-testid="archive-load-more"
                                className="px-9 py-3 rounded-full border border-line-strong bg-surface text-sm font-semibold text-foreground hover:border-accent/50 hover:text-accent hover:bg-accent-soft transition-colors duration-150"
                            >
                                Load More
                            </button>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}
